import React from 'react'
import { GetStaticProps, GetStaticPropsContext } from 'next'

import { PagesApi } from 'services/api/pages'
import { HeadMetadata } from '@components/head-metadata'
import { MenusApi, Menu } from '@services/api/menus'
import { BlockCallsApi, BlockCall } from '@services/api/block-calls'
import Image from 'next/image'
import { Layout } from '@components/layout'
import dynamic from 'next/dynamic'

import { PageShowDto } from '@services/api/pages/types'
import { AdsApi } from '@services/api/ads'
import { AdsIndexDto } from '@services/api/ads/types'
import { BreadcrumbPage } from '@components/breadcrumb-page'
import { RichSnippetNewsPage } from '@components/shared/rich-snippets/Page'

import styles from '@components/article/styles.module.scss'
import { ArticlePublication } from '@components/article/article-publication'
import { AuthorFooter } from '@components/article/author-footer'
import { parsedContent } from '@utils/editorjs-parse'
const ParseContentEditorJs = dynamic(
  () => import('@components/shared/editor-js-content/parse-content-editor-js'),
  { ssr: false }
)
interface Props {
  page: PageShowDto
  main: Menu[]
  footer1: Menu[]
  footer2: Menu[]
  ads: AdsIndexDto[]
  blockCallType01?: BlockCall[]
  blockCallSimulator?: BlockCall
}

const ArticlePage = ({
  page,
  main,
  footer1,
  footer2,
  ads,
  blockCallType01,
  blockCallSimulator
}: Props) => {
  return (
    <div className={styles.articleContainer}>
      <Layout
        mainMenus={main}
        footerMenus1={footer1}
        footerMenus2={footer2}
        ad={ads?.find(ad => ad.code === 'ad_sponsor_top')}
      >
        <HeadMetadata
          title={page?.metaTitle || page?.title || ''}
          metaTitle={page?.metaTitle || page?.title}
          metaDescription={page?.metaDescription}
          canonicalUrl={page?.canonicalUrl}
        />
        <RichSnippetNewsPage
          title={page?.title}
          dateModified={page?.updatedAt}
          datePublished={page?.createdAt}
          author={[]}
        />
        <main
          className="block mb-40"
          style={{
            maxWidth: page.pageWidth,
            width: '100%',
            margin: '0 auto'
          }}
        >
          <BreadcrumbPage breadcrumb={page.breadcrumbArray} />
          <section className="flex flex-col items-center m-auto pb-4 pt-10 font-serif">
            <header
              style={{
                maxWidth: '100%',
                width: '100%'
              }}
            >
              <h1
                className="text-4xl font-bold leading-11 mb-16 text-center md:break-normal"
                style={{ overflowWrap: 'break-word' }}
              >
                {page?.title}
              </h1>
            </header>

            <div className="flex flex-col items-center w-full">
              <section className={styles.articleHeader}>
                <div
                  className={`${styles.publicationInfo} flex justify-center w-full`}
                >
                  <ArticlePublication
                    authors={page?.authors}
                    publicationDate={page?.createdAt}
                    title={page?.title}
                    description={page.metaDescription}
                  />
                </div>
              </section>
              <div className="w-full flex flex-col items-center">
                <ParseContentEditorJs
                  raw={page?.content}
                  blockCallType01={blockCallType01}
                  pageWidth={page.pageWidth}
                  className="font-serif mt-2"
                  banner={blockCallSimulator}
                  adMiddle={ads?.find(ad => ad.code === 'ad_sponsor_middle')}
                  adBottom={ads?.find(ad => ad.code === 'ad_sponsor_bottom')}
                />
              </div>
            </div>
          </section>
          {page?.authors?.map(author => {
            return (
              <AuthorFooter
                key={author.name}
                name={author.name}
                path={author.path}
                avatar={author.avatar}
                biography={author.biography}
              />
            )
          })}
          <div className="flex justify-center items-center mt-10">
            <Image
              src="/assets/raio-laranja.svg"
              width={48}
              height={48}
              alt=""
              loading="lazy"
            />
          </div>
        </main>
      </Layout>
    </div>
  )
}

export async function getStaticPaths() {
  return { paths: [], fallback: 'blocking' }
}

export const getStaticProps: GetStaticProps = async (
  context: GetStaticPropsContext<{ slugs: string[] }>
) => {
  try {
    const { slugs } = context.params
    if (!slugs?.length) {
      return { notFound: true }
    }
    const fullPath = slugs.join('/')
    const finalSlug = slugs[slugs.length - 1]
    const pageSlug = finalSlug.replace('.html', '%2Ehtml')
    const pageSlugFullPath = fullPath.replace('.html', '%2Ehtml')

    const [data, menus, blockCall, blockCallSimulator, adsResponse] =
      await Promise.allSettled([
        PagesApi.show(pageSlugFullPath),
        MenusApi.all(),
        BlockCallsApi.index({ block_type: 'type01' }),
        BlockCallsApi.random({
          amount: 1,
          block_type: 'simulator'
        }),
        AdsApi.index({
          codes: ['ad_sponsor_top', 'ad_sponsor_middle', 'ad_sponsor_bottom']
        })
      ])

    let pageData = data.status === 'fulfilled' ? data.value?.data : undefined

    if (!pageData && pageSlugFullPath !== pageSlug) {
      const retry = await PagesApi.show(pageSlug).catch(() => null)
      if (retry?.data) pageData = retry.data
    }

    const { breadcrumbArray = [] } = pageData || {}
    const breadcrumbPath = breadcrumbArray
      .map((item: { slug: string }) => item.slug)
      .join('/')
    const isValidUrl = fullPath === breadcrumbPath

    if (!pageData || !isValidUrl) {
      // Sem revalidate, uma falha momentânea da API deixa o 404 em cache até o próximo deploy.
      return {
        notFound: true,
        revalidate: 60
      }
    }

    const ads =
      adsResponse?.status === 'fulfilled' && adsResponse.value?.data?.ads
        ? adsResponse.value.data.ads
        : []

    return {
      props: {
        page: pageData,
        ...menus.value,
        ads,
        blockCallType01: blockCall?.value?.data?.blockCalls,
        blockCallSimulator:
          blockCallSimulator?.value?.data?.length > 0
            ? blockCallSimulator?.value?.data[0]
            : null
      },
      revalidate: 60 * 10
    }
  } catch (error) {
    return {
      redirect: {
        destination: '/404',
        permanent: false
      },
      revalidate: 1
    }
  }
}

export default ArticlePage
